import { BaseListStore } from './baseListStore';
import { AbstractBaseListStore } from './abstractBaseListStore';
import { IFaceData } from '@/editor/data/face/face';
import { RoomBaseData, RoomData, RoomExtraData } from '@/editor/data/room/Room';
import { TGlobalStores } from '..';
import { EEntityType, IWallEdge } from '@/editor/types';

export class BaseRoomStore implements AbstractBaseListStore<RoomData> {
  private _roomBaseDataMap;
  private _roomExtraDataMap;

  constructor(gs: TGlobalStores) {
    this._roomBaseDataMap = new BaseListStore<RoomBaseData & { corePathInfos: IWallEdge[] }>(gs, {
      dataType: EEntityType.Room,
      onDataChanged: (changedId) => this._syncExtraData(changedId)
    });
    this._roomExtraDataMap = new Map<string, RoomExtraData>();
  }

  /**
   * 让 extra 表跟着 base 表同生同死。
   *
   * 只有 base 表接了 history，undo / redo 经 applyChange 直接调 BaseListStore 的 add / remove，
   * 绕过本类的 add / remove，extra 表不会跟着动。撤销删墙时 base 被恢复、extra 却是空的，
   * 该房间就从 getList() 里消失，refreshRoomDatas 认领不到它，只能新建一个 id ——
   * 名称/材质/offGround 全部回默认，旧 base 数据还留下来成为孤儿(deleteRoomSet 也是按 getList() 建的)。
   *
   * 补的占位 extra 与 coverDatas() 加载方案时一致：几何留空，corePathInfos 取 base 里那份
   * (由 updateBaseCorePathInfos 维护，见下)，紧接着的 refreshRoomDatas 认领到之后会用重算结果覆盖掉。
   */
  private _syncExtraData(changedId?: string) {
    // clear / addAll 是整表替换，不带 id，按全量对齐
    if (changedId === undefined) {
      const baseIds = new Set(this._roomBaseDataMap.getList().map((item) => item.id));
      this._roomExtraDataMap.forEach((_, id) => {
        if (!baseIds.has(id)) this._roomExtraDataMap.delete(id);
      });
      baseIds.forEach((id) => this._syncExtraData(id));
      return;
    }

    const baseData = this._roomBaseDataMap.get(changedId);
    if (!baseData) {
      this._roomExtraDataMap.delete(changedId);
      return;
    }
    if (this._roomExtraDataMap.has(changedId)) return;

    this._roomExtraDataMap.set(changedId, {
      height: 0,
      pathInfos: [],
      holeInfos: [],
      corePathInfos: baseData.corePathInfos,
      coreHoleInfos: []
    });
  }

  add(data: RoomData) {
    this._roomBaseDataMap.add({
      id: data.id,
      offGround: data.offGround,
      // wallDatas: data.wallDatas,
      roomInfo: data.roomInfo,
      materialInfo: data.materialInfo,
      relateWallIds: data.relateWallIds,
      corePathInfos: data.corePathInfos
    });
    this._roomExtraDataMap.set(data.id, {
      height: data.height,
      pathInfos: data.pathInfos,
      holeInfos: data.holeInfos,
      corePathInfos: data.corePathInfos,
      coreHoleInfos: data.coreHoleInfos
    });
  }

  update(_id: string, _data: IFaceData) {
    console.warn('can not update room data directly');
  }

  updateBaseData(id: string, data: DeepPartial<RoomBaseData>) {
    if (!id || !data) return;
    const currentData = this._roomBaseDataMap.get(id);
    if (!currentData) return;
    this._roomBaseDataMap.update(id, data);
  }

  /**
   * base 表里那一行（不含 extra）。
   *
   * 认领旧房间（refreshRoomDatas → matchRoomsByCorePath）必须读这里的 corePathInfos，不能读
   * get() / getList() 拼出来的那份——那份是 extra 的值盖在 base 上，而 extra 不进 history，
   * 撤销后会停留在撤销前那一版。
   */
  getBaseData(id: string) {
    return this._roomBaseDataMap.get(id);
  }

  /**
   * 回写 base 表里的壁芯环。
   *
   * base 表一直存着这一份（见构造函数的泛型），但只在 add 时写过一次。它是认领旧房间的 key，
   * 而只有 base 进 history：不跟着刷新的话，撤销后拿到的是「创建那一刻」的老环，
   * 跟着 extra 走的话，撤销后拿到的是「撤销前」的新环（可能含已经被撤销掉的墙 id，
   * 兜底轮的「旧边集 ⊆ 新边集」永远过不了）。两头都不对，所以这一份必须由 base 自己维护。
   */
  updateBaseCorePathInfos(id: string, corePathInfos: IWallEdge[]) {
    if (!this._roomBaseDataMap.get(id)) return;
    this._roomBaseDataMap.update(id, { corePathInfos });
  }

  updateExtraData(id: string, data: RoomExtraData) {
    if (!id || !data) return;
    this._roomExtraDataMap.set(id, data);
  }

  remove(id: string) {
    if (!id || !this._roomBaseDataMap.get(id)) return;

    this._roomBaseDataMap.remove(id);
    this._roomExtraDataMap.delete(id);
  }

  get(id: string): RoomData | undefined {
    const baseData = this._roomBaseDataMap.get(id);
    if (!baseData) return;
    // _syncExtraData 保证 base 在则 extra 在，取不到属于程序错误，按取不到处理不再往下拼半份数据
    const extraData = this._roomExtraDataMap.get(id);
    if (!extraData) return;

    return {
      ...baseData,
      ...extraData
    };
  }

  getList() {
    return this._roomBaseDataMap.getList().flatMap((item) => {
      const extraData = this._roomExtraDataMap.get(item.id);
      return extraData ? [{ ...item, ...extraData }] : [];
    });
  }

  clear() {
    this._roomBaseDataMap.clear();
    this._roomExtraDataMap.clear();
  }
}
